import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../Api/api';
import { getAuthToken, isPartnerRoute, getAuthUser } from '../../Api/authToken';
import {
  hasMapPin,
  getMapPinLabel,
  getPropertyStatusLabel,
} from '../../utils/locationFormat';
import PropertyLocationDetailModal, { resolvePropertyThumb } from './PropertyLocationDetailModal';
import './AdminPropertyLocationsMap.css';

const STATUS_COLORS = { 1: '#f59e0b', 2: '#16a34a', 3: '#ef4444', 4: '#7c3aed' };
const MAP_FETCH_LIMIT = 500;

const escapeHtml = (str) => String(str ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const loadLeaflet = () => new Promise((resolve, reject) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    reject(new Error('Leaflet can only load in the browser.'));
    return;
  }
  if (window.L) return resolve(window.L);
  if (!document.getElementById('leaflet-css')) {
    const link = document.createElement('link');
    link.id = 'leaflet-css';
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }
  if (document.getElementById('leaflet-js')) {
    let tries = 0;
    const check = setInterval(() => {
      if (window.L) {
        clearInterval(check);
        resolve(window.L);
      } else if (tries++ > 100) {
        clearInterval(check);
        reject(new Error('Leaflet script did not initialize.'));
      }
    }, 50);
    return;
  }
  const script = document.createElement('script');
  script.id = 'leaflet-js';
  script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  script.onload = () => resolve(window.L);
  script.onerror = () => reject(new Error('Failed to load Leaflet script.'));
  document.head.appendChild(script);
});

const buildMarkerIcon = (L, statusId) => {
  const color = STATUS_COLORS[statusId] || '#64748b';
  return L.divIcon({
    className: 'aplm-marker-wrap',
    html: `<div class="aplm-marker" style="background:${color}"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
};

const buildTooltipHtml = (property) => {
  const thumb = resolvePropertyThumb(property.thumbnail);
  const loc = getMapPinLabel(property);
  const user = property.creator_name || 'Unknown';
  const status = getPropertyStatusLabel(property.status_id);
  return `
    <div class="aplm-tooltip-inner">
      <img src="${escapeHtml(thumb)}" alt="" class="aplm-tooltip-img" />
      <p class="aplm-tooltip-loc">${escapeHtml(loc)}</p>
      <p class="aplm-tooltip-user">${escapeHtml(user)}</p>
      <span class="pml-status pml-status-${property.status_id}">${escapeHtml(status)}</span>
    </div>
  `;
};

const filterForPartner = (list) => {
  if (!isPartnerRoute()) return list;
  const partner = getAuthUser();
  let subs = partner?.sub_registers;
  if (!subs?.length) return list;
  if (typeof subs === 'string') subs = subs.split(',').map(Number);
  if (!Array.isArray(subs)) return list;
  subs = subs.map(Number).filter(Boolean);
  if (!subs.length) return list;
  return list.filter((p) => {
    const subId = Number(p.sub_register_id ?? p.extra_fields?.sub_register_id);
    return subs.includes(subId);
  });
};

// Default filters used when the component is rendered as a standalone page
const STANDALONE_FILTERS = {
  search: '', location_search: '', role: '', is_project: '',
  listing_type: '', status_id: '', is_urgent: '', is_highlighted: '',
  is_draft: '', state_id: '', city_id: '', sub_register_id: [],
  date_from: '', date_to: '', price_exact: '', price_min: '', price_max: '',
  page: 1, limit: MAP_FETCH_LIMIT,
};

const AdminPropertyLocationsMap = ({
  filters,
  isAwaiting = false,
  isOwn = false,
  isMine = false,
  states = [],
  cities = [],
  subRegisters = [],
  // When true the component renders with its own page header (standalone route)
  standalone = false,
}) => {
  const mapDivRef   = useRef(null);
  const mapRef      = useRef(null);
  const markersRef  = useRef(null);
  const [mapPins, setMapPins]             = useState([]);
  const [mapLoading, setMapLoading]       = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [mapReady, setMapReady]           = useState(false);
  const [mapError, setMapError]           = useState('');
  const [locationQuery, setLocationQuery] = useState('');

  // Internal lookups — only fetched when the component is used standalone
  // (i.e. no states/cities/subRegisters passed as props)
  const [internalStates, setInternalStates]               = useState([]);
  const [internalCities, setInternalCities]               = useState([]);
  const [internalSubRegisters, setInternalSubRegisters]   = useState([]);

  useEffect(() => {
    if (!states.length)
      axios.get(`${API_URL}/states`).then(r => setInternalStates(r.data)).catch(() => {});
    if (!cities.length)
      axios.get(`${API_URL}/cities`).then(r => setInternalCities(r.data)).catch(() => {});
    if (!subRegisters.length)
      axios.get(`${API_URL}/subregisters`).then(r => setInternalSubRegisters(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedStates       = states.length       ? states       : internalStates;
  const resolvedCities       = cities.length       ? cities       : internalCities;
  const resolvedSubRegisters = subRegisters.length ? subRegisters : internalSubRegisters;

  // Use provided filters or fall back to the standalone defaults
  const standaloneFilters = useMemo(
    () => ({ ...STANDALONE_FILTERS, search: locationQuery }),
    [locationQuery],
  );
  const activeFilters = filters ?? standaloneFilters;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    const timer = setTimeout(async () => {
      setMapLoading(true);
      try {
        const token = getAuthToken();
        const queryParams = { ...activeFilters, page: 1, limit: MAP_FETCH_LIMIT, map_pins: '1' };
        const rawSearch = String(queryParams.search || '').replace(/^#/, '').trim();
        if (/^\d+$/.test(rawSearch)) {
          delete queryParams.search;
          queryParams.exact_property_id = rawSearch;
        }
        const subIds = Array.isArray(queryParams.sub_register_id)
          ? queryParams.sub_register_id
          : [];
        if (subIds.length > 0) {
          queryParams.sub_register_id = subIds.join(',');
        } else {
          delete queryParams.sub_register_id;
        }
        Object.keys(queryParams).forEach((key) => {
          if (queryParams[key] === '' || queryParams[key] == null) {
            delete queryParams[key];
          }
        });
        if (isAwaiting) queryParams.status_id = '1';
        if (isMine) queryParams.my_listings = true;
        else if (isOwn) queryParams.my_properties = true;

        const res = await axios.get(`${API_URL}/admin/listings`, {
          headers: { Authorization: `Bearer ${token}` },
          params: queryParams,
          signal: controller.signal,
        });
        if (cancelled) return;
        let rows = Array.isArray(res?.data?.listings) ? res.data.listings : [];
        if (isMine) {
          const staff = getAuthUser();
          const asPartner = isPartnerRoute();
          rows = rows.filter((prop) => {
            if (!staff) return false;
            if (asPartner) {
              const pid = Number(staff.id);
              const raw = prop.partner_id;
              const creator = Array.isArray(raw) ? Number(raw[0]) : Number(raw);
              return Number.isFinite(pid) && pid > 0 && creator === pid;
            }
            const ids = [staff.id, staff.admin_id].map(Number).filter((n) => Number.isFinite(n) && n > 0);
            return ids.includes(Number(prop.admin_id));
          });
        }
        const withCoords = rows.filter(hasMapPin);
        setMapPins(filterForPartner(withCoords));
      } catch (err) {
        if (
          cancelled
          || err?.code === 'ERR_CANCELED'
          || err?.name === 'CanceledError'
          || err?.name === 'AbortError'
        ) {
          return;
        }
        setMapPins([]);
      } finally {
        if (!cancelled) setMapLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [activeFilters, isAwaiting, isOwn, isMine]);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapDivRef.current) return;
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
        const map = L.map(mapDivRef.current, { zoomControl: true }).setView([20.5937, 78.9629], 5);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
        }).addTo(map);
        mapRef.current = map;
        markersRef.current = L.layerGroup().addTo(map);
        setMapError('');
        setMapReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Leaflet map failed to load:', err);
        setMapReady(false);
        setMapError('Map preview could not be loaded right now.');
      });
    return () => {
      cancelled = true;
      setMapReady(false);
      markersRef.current = null;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.L) return;
    const L = window.L;
    const group = markersRef.current;
    if (!group) return;

    try {
      group.clearLayers();

      const validPins = [];
      mapPins.forEach((property) => {
        const lat = Number(property.latitude);
        const lng = Number(property.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const marker = L.marker([lat, lng], {
          icon: buildMarkerIcon(L, property.status_id),
        });
        marker.bindTooltip(buildTooltipHtml(property), {
          direction: 'top',
          offset: [0, -12],
          className: 'aplm-leaflet-tooltip',
          opacity: 1,
        });
        marker.on('click', () => setSelectedProperty(property));
        group.addLayer(marker);
        validPins.push([lat, lng]);
      });

      if (validPins.length === 1) {
        mapRef.current.setView(validPins[0], 14);
      } else if (validPins.length > 1) {
        const bounds = L.latLngBounds(validPins);
        mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      } else {
        mapRef.current.setView([20.5937, 78.9629], 5);
      }
    } catch (err) {
      console.error('Failed to render map pins:', err);
    }
  }, [mapPins, mapReady]);

  const mapSection = (
    <div className="admin-card aplm-section">
      <div className="aplm-header">
        <div>
          <h2 className="aplm-title">All Property Locations</h2>
          <p className="aplm-sub">
            Map pins from coordinates selected when users upload properties.
            {mapLoading ? ' Loading…' : ` ${mapPins.length} location${mapPins.length === 1 ? '' : 's'} on map.`}
          </p>
          {isPartnerRoute() && (
            <p className="aplm-partner-note">Showing locations for your assigned sub-registers only.</p>
          )}
        </div>
        <div className="aplm-legend">
          <span><i style={{ background: STATUS_COLORS[1] }} /> Awaiting</span>
          <span><i style={{ background: STATUS_COLORS[2] }} /> Approved</span>
          <span><i style={{ background: STATUS_COLORS[3] }} /> Rejected</span>
          <span><i style={{ background: STATUS_COLORS[4] }} /> Sold Out</span>
        </div>
      </div>

      <div className="aplm-map-wrap">
        {mapLoading && <div className="aplm-map-loading">Loading map locations…</div>}
        {!mapLoading && mapError && (
          <div className="aplm-map-empty">{mapError}</div>
        )}
        {!mapLoading && mapPins.length === 0 && (
          <div className="aplm-map-empty">No properties with map coordinates match the current filters.</div>
        )}
        <div ref={mapDivRef} className="aplm-map" />
      </div>

      {selectedProperty && (
        <PropertyLocationDetailModal
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
          states={resolvedStates}
          cities={resolvedCities}
          subRegisters={resolvedSubRegisters}
        />
      )}
    </div>
  );

  // When used as a standalone sidebar page, wrap with a page shell
  if (standalone) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <div>
            <h1>All Property Locations</h1>
            <p>Map view of every property that has GPS coordinates on record.</p>
          </div>
        </div>
        <label className="aplm-search">
          <input
            value={locationQuery}
            onChange={(event) => setLocationQuery(event.target.value)}
            placeholder="Search by property ID or location"
            aria-label="Search property locations"
          />
        </label>
        {mapSection}
      </div>
    );
  }

  return mapSection;
};

export default AdminPropertyLocationsMap;